import React from "react";
import { makeStyles } from "@Compat/makeStyles";
import { siteTokens } from "../siteTokens";

// The founder's face on the page (founder note, demo drawer, chat header).
// No photo yet, so it renders a monogram. To use a photo, add a square WebP
// (at least 320 px) as assets/people/vasco-galvao.webp, import it here and
// set FOUNDER_PHOTO to it — every place that shows the founder picks it up.
const FOUNDER_PHOTO: string | undefined = undefined;
const FOUNDER_INITIALS = "VG";

const useStyles = makeStyles((theme) => {
  const tk = siteTokens(theme);
  return {
  root: {
    flexShrink: 0,
    borderRadius: "50%",
    overflow: "hidden",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: tk.onAccent,
    fontWeight: 800,
    letterSpacing: 0.5,
    lineHeight: 1,
    background: `linear-gradient(135deg, ${tk.accent} 0%, ${tk.accent2} 100%)`,
    "& img": { width: "100%", height: "100%", objectFit: "cover", display: "block" }
  },
  // For avatars sitting on a card: a ring in the card colour + soft shadow.
  ring: {
    boxShadow: `0 0 0 3px ${tk.surface}, 0 12px 28px ${tk.accentDeepA(0.28)}`
  }
  };
});

interface FounderAvatarProps {
  /** Rendered diameter in px. */
  size?: number;
  /** Accessible name; omit when the founder's name is printed next to it. */
  alt?: string;
  ring?: boolean;
  className?: string;
}

const FounderAvatar: React.FC<FounderAvatarProps> = ({ size = 40, alt, ring = false, className }) => {
  const classes = useStyles();
  const classNames = [classes.root, ring ? classes.ring : "", className ?? ""].filter(Boolean).join(" ");
  return (
    <span
      className={classNames}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
      role={alt ? "img" : undefined}
      aria-label={alt}
      aria-hidden={alt ? undefined : true}
    >
      {FOUNDER_PHOTO
        ? <img src={FOUNDER_PHOTO} alt="" width={size} height={size} loading="lazy" decoding="async" />
        : FOUNDER_INITIALS}
    </span>
  );
};

export default FounderAvatar;
